import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useSOSHelpers } from '@/hooks/useSOSHelpers';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { MapPin, Users, Clock, Phone, Share2, CheckCircle2, XCircle, Trash2, AlertTriangle, Edit, MessageCircle, MapPinPlus } from 'lucide-react';

interface SOSReport {
  id: string;
  user_id: string;
  type: 'lost' | 'found' | 'emergency' | 'danger';
  title: string;
  description: string | null;
  animal_species: string | null;
  animal_breed: string | null;
  animal_name: string | null;
  image_url: string | null;
  latitude: number;
  longitude: number;
  location_precision: 'precise' | 'street' | 'approximate';
  status: 'open' | 'spotted' | 'in_progress' | 'resolved' | 'cancelled';
  contact_phone: string | null;
  allow_public_sharing: boolean;
  last_spotted_at: string | null;
  helper_count: number;
  created_at: string;
  updated_at: string;
  profiles?: {
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  };
}

interface SOSDetailModalProps {
  sos: SOSReport | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: () => void;
  onEdit?: (sos: SOSReport) => void;
}

export const SOSDetailModal = ({ sos, open, onOpenChange, onUpdate, onEdit }: SOSDetailModalProps) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [showUpdateLocationDialog, setShowUpdateLocationDialog] = useState(false);
  const [newLocationDescription, setNewLocationDescription] = useState('');
  const [gettingLocation, setGettingLocation] = useState(false);
  const [newLatitude, setNewLatitude] = useState<number | null>(null);
  const [newLongitude, setNewLongitude] = useState<number | null>(null);
  const { isHelping, helpers, loading: helpLoading, toggleHelp, helperCount } = useSOSHelpers(sos?.id || null);

  if (!sos) return null;

  const isOwner = user?.id === sos.user_id;

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'lost': return '🐕';
      case 'found': return '🔍';
      case 'emergency': return '🚨';
      case 'danger': return '⚠️';
      default: return '📍';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'lost': return 'Animale smarrito';
      case 'found': return 'Animale trovato';
      case 'emergency': return 'Emergenza';
      case 'danger': return 'Situazione di pericolo';
      default: return 'Generale';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'open': return 'Aperto';
      case 'spotted': return 'Avvistato';
      case 'in_progress': return 'In corso';
      case 'resolved': return 'Risolto';
      case 'cancelled': return 'Annullato';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'destructive';
      case 'spotted': return 'secondary';
      case 'in_progress': return 'default';
      case 'resolved': return 'outline';
      case 'cancelled': return 'secondary';
      default: return 'secondary';
    }
  };

  const handleResolve = async () => {
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('sos_reports')
        .update({ 
          status: 'resolved',
          updated_at: new Date().toISOString()
        })
        .eq('id', sos.id);

      if (error) throw error;

      toast({
        title: "SOS risolto!",
        description: "Ottimo! L'SOS è stato contrassegnato come risolto.",
      });

      onUpdate();
      onOpenChange(false);
      setShowResolveDialog(false);
    } catch (error) {
      console.error('Error resolving SOS:', error);
      toast({
        title: "Errore",
        description: "Impossibile risolvere l'SOS. Riprova.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('sos_reports')
        .delete()
        .eq('id', sos.id);

      if (error) throw error;

      toast({
        title: "SOS eliminato",
        description: "L'SOS è stato eliminato con successo.",
      });

      onUpdate();
      onOpenChange(false);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting SOS:', error);
      toast({
        title: "Errore",
        description: "Impossibile eliminare l'SOS. Riprova.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };


  const handleShare = async () => {
    const link = `${window.location.origin}/app/sos?sosId=${sos.id}`;
    try {
      await navigator.clipboard.writeText(link);
      toast({
        title: "Link copiato!",
        description: "Link diretto alla lista SOS copiato negli appunti",
      });
    } catch (e) {
      console.error('Clipboard error:', e);
      toast({
        title: "Errore",
        description: "Impossibile copiare il link",
        variant: "destructive",
      });
    }
  };

  const getCurrentLocationForSighting = async () => {
    setGettingLocation(true);
    try {
      if (!navigator.geolocation) {
        toast({
          title: "Errore",
          description: "Geolocalizzazione non supportata dal browser",
          variant: "destructive"
        });
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          setNewLatitude(position.coords.latitude);
          setNewLongitude(position.coords.longitude);
          toast({
            title: "Posizione ottenuta",
            description: "Posizione corrente acquisita con successo"
          });
          setGettingLocation(false);
        },
        (error) => {
          console.error('Geolocation error:', error);
          toast({
            title: "Errore",
            description: "Impossibile ottenere la posizione corrente",
            variant: "destructive"
          });
          setGettingLocation(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } catch (error) {
      console.error('Error getting location:', error);
      setGettingLocation(false);
    }
  };

  const handleUpdateLocation = async () => {
    if (!newLatitude || !newLongitude) {
      toast({
        title: "Errore",
        description: "Devi prima ottenere la posizione corrente",
        variant: "destructive"
      });
      return;
    }

    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('sos_sightings')
        .insert({
          sos_report_id: sos.id,
          user_id: user?.id,
          latitude: newLatitude,
          longitude: newLongitude,
          description: newLocationDescription,
          sighted_at: new Date().toISOString()
        });

      if (error) throw error;

      // Update SOS report with new location
      const { error: updateError } = await supabase
        .from('sos_reports')
        .update({ 
          latitude: newLatitude,
          longitude: newLongitude,
          last_spotted_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', sos.id);

      if (updateError) throw updateError;

      toast({
        title: "Posizione aggiornata!",
        description: "Grazie per aver segnalato il nuovo avvistamento",
      });

      setShowUpdateLocationDialog(false);
      setNewLocationDescription('');
      setNewLatitude(null);
      setNewLongitude(null);
      onUpdate();
    } catch (error) {
      console.error('Error updating location:', error);
      toast({
        title: "Errore",
        description: "Impossibile aggiornare la posizione. Riprova.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto z-50">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="text-2xl">{getTypeIcon(sos.type)}</span>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {sos.title}
                  <Badge variant={getStatusColor(sos.status)}>
                    {getStatusLabel(sos.status)}
                  </Badge>
                </div>
                <div className="text-sm text-muted-foreground font-normal">
                  {getTypeLabel(sos.type)}
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Image */}
            {sos.image_url && (
              <div className="w-full">
                <img 
                  src={sos.image_url} 
                  alt={sos.title}
                  className="w-full h-64 object-cover rounded-lg"
                />
              </div>
            )}

            {/* Animal Info */}
            {sos.animal_species && (
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-2">🐾 Informazioni animale</h3>
                  <div className="space-y-1 text-sm">
                    <div><strong>Specie:</strong> {sos.animal_species}</div>
                    {sos.animal_breed && <div><strong>Razza:</strong> {sos.animal_breed}</div>}
                    {sos.animal_name && <div><strong>Nome:</strong> {sos.animal_name}</div>}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Description */}
            {sos.description && (
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-2">📝 Descrizione</h3>
                  <p className="text-sm">{sos.description}</p>
                </CardContent>
              </Card>
            )}

            {/* Location & Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4" />
                    <span className="font-semibold">Posizione</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <div>Lat: {sos.latitude.toFixed(6)}</div>
                    <div>Lng: {sos.longitude.toFixed(6)}</div>
                    <div>Precisione: {sos.location_precision}</div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4" />
                    <span className="font-semibold">Aiuto</span>
                  </div>
            <div className="text-sm text-muted-foreground">
              <div>{helperCount} persone stanno aiutando</div>
              <div className="flex items-center gap-1 mt-1">
                <Clock className="w-3 h-3" />
                {new Date(sos.created_at).toLocaleDateString('it-IT')}
              </div>
            </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact */}
            {sos.contact_phone && (
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Phone className="w-4 h-4" />
                    <span className="font-semibold">Contatto</span>
                  </div>
                  <div className="text-sm">
                    <a href={`tel:${sos.contact_phone}`} className="text-primary hover:underline">
                      {sos.contact_phone}
                    </a>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Helpers List - only if user is helping or is owner */}
            {(isHelping || isOwner) && helpers.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="w-4 h-4" />
                    <span className="font-semibold">Chi sta aiutando ({helpers.length})</span>
                  </div>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {helpers.map((helper) => (
                      <div key={helper.id} className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          {helper.profiles?.avatar_url ? (
                            <img 
                              src={helper.profiles.avatar_url} 
                              alt="Avatar" 
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <Users className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">
                            {helper.profiles?.display_name || helper.profiles?.username || 'Utente'}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Si è unito {new Date(helper.created_at).toLocaleString('it-IT')}
                          </div>
                        </div>
                        {isOwner && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(`/app/messages?user=${helper.profiles?.username}`, '_blank')}
                          >
                            <MessageCircle className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Help Instructions */}
            {isHelping && (
              <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div className="space-y-2">
                      <h4 className="font-semibold text-blue-800 dark:text-blue-200">
                        Stai aiutando! Ecco cosa puoi fare:
                      </h4>
                      <div className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                        <p>• Condividi la tua posizione per coordinare le ricerche</p>
                        <p>• Mantieni attiva la condivisione posizione durante l'aiuto</p>
                        <p>• Contatta il segnalatore per coordinarti</p>
                        {sos.type === 'lost' && <p>• Cerca nell'area e segnala avvistamenti</p>}
                        {sos.type === 'found' && <p>• Aiuta a riunire l'animale con il proprietario</p>}
                        {(sos.type === 'emergency' || sos.type === 'danger') && (
                          <p>• In caso di emergenza, chiama subito il 112</p>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              {!isOwner && sos.status === 'open' && (
                <>
                  <Button 
                    onClick={async () => {
                      await toggleHelp();
                      // Don't call onUpdate() here - real-time updates will sync automatically
                    }}
                    disabled={helpLoading}
                    variant={isHelping ? "secondary" : "default"}
                    className="flex-1 min-w-[180px]"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    {helpLoading ? 'Caricamento...' : isHelping ? 'Non aiutare più' : 'Mi unisco all\'aiuto'}
                  </Button>
                  
                  {isHelping && (
                    <Button 
                      onClick={() => setShowUpdateLocationDialog(true)}
                      variant="outline"
                      className="flex-1 min-w-[180px]"
                    >
                      <MapPinPlus className="w-4 h-4 mr-2" />
                      Aggiorna posizione
                    </Button>
                  )}
                </>
              )}

              <Button 
                variant="outline"
                onClick={handleShare}
                className="flex-1"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Condividi
              </Button>

              {isOwner && sos.status === 'open' && (
                <>
                  <Button 
                    onClick={() => onEdit?.(sos)}
                    disabled={loading}
                    variant="outline"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Modifica
                  </Button>

                  <Button 
                    onClick={() => setShowResolveDialog(true)}
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Segna come risolto
                  </Button>
                  
                  <Button 
                    variant="destructive"
                    onClick={() => setShowDeleteDialog(true)}
                    disabled={loading}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Elimina
                  </Button>
                </>
              )}
            </div>

            {/* Warning for emergencies */}
            {(sos.type === 'emergency' || sos.type === 'danger') && (
              <Card className="border-orange-200 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-800">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
                    <div className="space-y-2">
                      <h4 className="font-semibold text-orange-800 dark:text-orange-200">
                        Emergenza - Consigli di sicurezza
                      </h4>
                      <div className="text-sm text-orange-700 dark:text-orange-300 space-y-1">
                        <p>• Mantieni sempre una distanza di sicurezza</p>
                        <p>• In caso di emergenza veterinaria, chiama il 112</p>
                        <p>• Non cercare di gestire animali aggressivi da solo</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Resolve Confirmation Dialog */}
      <AlertDialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Segna come risolto</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler segnare questo SOS come risolto? 
              Questa azione indicherà che la situazione è stata gestita con successo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleResolve}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700"
            >
              {loading ? 'Aggiornamento...' : 'Segna come risolto'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Update Location Dialog */}
      <AlertDialog open={showUpdateLocationDialog} onOpenChange={setShowUpdateLocationDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Aggiorna posizione avvistamento</AlertDialogTitle>
            <AlertDialogDescription>
              Hai avvistato l'animale in una nuova posizione? Segnalalo per aiutare nelle ricerche.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Posizione corrente</Label>
              <Button 
                type="button"
                variant="outline"
                onClick={getCurrentLocationForSighting}
                disabled={gettingLocation}
                className="w-full mt-2"
              >
                <MapPin className="w-4 h-4 mr-2" />
                {gettingLocation ? 'Rilevamento...' : newLatitude ? `${newLatitude.toFixed(6)}, ${newLongitude?.toFixed(6)}` : 'Ottieni posizione corrente'}
              </Button>
            </div>
            <div>
              <Label htmlFor="sighting-description">Descrizione (opzionale)</Label>
              <Textarea
                id="sighting-description"
                value={newLocationDescription}
                onChange={(e) => setNewLocationDescription(e.target.value)}
                placeholder="Descrivi cosa hai visto, in che condizioni era l'animale, etc."
                className="mt-2"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleUpdateLocation}
              disabled={loading || !newLatitude}
            >
              {loading ? 'Aggiornamento...' : 'Segnala avvistamento'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina SOS</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare questo SOS? 
              Questa azione non può essere annullata e tutti i dati associati verranno persi.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              disabled={loading}
              className="bg-destructive hover:bg-destructive/90"
            >
              {loading ? 'Eliminazione...' : 'Elimina definitivamente'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};